import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Sparkles, FileText, Link2, Loader2, Trash2, Paperclip, Download, Upload,
  ChevronDown, ChevronUp, Eye, Reply, Forward, ExternalLink, Mail, MailOpen,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { ExecInboxThread, ExecInboxStage, EXEC_INBOX_UNSORTED_STAGE_ID, CRMUser, EnhancedDeal, TimberProject } from '../../types';
import {
  getExecThread, clearDraft, linkThread, ExecMessage, downloadExecAttachment,
  markExecThreadRead, pushExecAttachment, acceptProposedLink, dismissProposedLink,
} from '../../utils/execInboxApi';
import { resolvePushTargets } from '../../utils/execInboxLinkResolve';
import ExecAttachmentPushModal from './ExecAttachmentPushModal';
import ExecAttachmentPreviewModal, { isPreviewable } from './ExecAttachmentPreviewModal';
import ExecComposer, { ComposeMode, textToHtml } from './ExecComposer';

export interface LinkCandidate { type: 'lead' | 'deal' | 'project'; id: string; name: string; }

interface Props {
  mailboxUserId: string;
  thread: ExecInboxThread;
  stages: ExecInboxStage[];
  ownerName: string;
  ownerEmail: string;
  senderName: string;
  isOwner: boolean;
  linkCandidates: LinkCandidate[];
  enhancedDeals: EnhancedDeal[];
  timberProjects: TimberProject[];
  crmUsers: CRMUser[];
  onClose: () => void;
  onMove: (stageId: string) => void;
  onDelete: () => void;
  onToast: (msg: string, error?: boolean) => void;
}

type ExecAttachment = NonNullable<ExecMessage['attachments']>[0];

function sanitize(html: string): string {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

function msgTimestamp(m: ExecMessage): number {
  if (m.internalDate) return m.internalDate;
  const d = Date.parse(m.date);
  return Number.isNaN(d) ? 0 : d;
}

function sortMessagesChrono(msgs: ExecMessage[]): ExecMessage[] {
  return [...msgs].sort((a, b) => msgTimestamp(a) - msgTimestamp(b));
}

function trimPlainQuotes(text: string): { content: string; hasQuote: boolean } {
  if (!text) return { content: '', hasQuote: false };
  const lines = text.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (/^On .+ wrote:$/i.test(t)) {
      return { content: lines.slice(0, i).join('\n').trim(), hasQuote: true };
    }
    if (/^-{2,}\s*Original Message\s*-{2,}/i.test(t)) {
      return { content: lines.slice(0, i).join('\n').trim(), hasQuote: true };
    }
    if (/^From:\s.+/i.test(t) && i + 1 < lines.length && /^Sent:\s/i.test(lines[i + 1]?.trim())) {
      return { content: lines.slice(0, i).join('\n').trim(), hasQuote: true };
    }
    if (t.startsWith('>')) {
      return { content: lines.slice(0, i).join('\n').trim(), hasQuote: true };
    }
  }
  return { content: text, hasQuote: false };
}

function trimHtmlQuotes(html: string): { content: string; hasQuote: boolean } {
  if (!html) return { content: '', hasQuote: false };
  const patterns = [
    /<blockquote[^>]*class="[^"]*gmail_quote/i,
    /<div[^>]*class="[^"]*gmail_quote/i,
    /<div[^>]*id="divRplyFwdMsg"/i,
    /<blockquote[^>]*type="cite"/i,
  ];
  for (const re of patterns) {
    const idx = html.search(re);
    if (idx > 0) return { content: html.slice(0, idx).trim(), hasQuote: true };
  }
  return { content: html, hasQuote: false };
}

function fmtAttachSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtMsgDate(m: ExecMessage): string {
  const ts = msgTimestamp(m);
  if (!ts) return m.date || '';
  return new Date(ts).toLocaleString();
}

interface QuickPush {
  label: string;
  push: (messageId: string, att: ExecAttachment) => Promise<void>;
}

interface MessageBlockProps {
  m: ExecMessage;
  isLatest?: boolean;
  isOutbound?: boolean;
  downloadingId: string | null;
  pushingId: string | null;
  quickPush: QuickPush | null;
  onDownload: (messageId: string, att: ExecAttachment) => void;
  onPreview: (target: { messageId: string; attachment: ExecAttachment }) => void;
  onPush: (target: { messageId: string; messageDate?: string; attachment: ExecAttachment }) => void;
  onCompose: (mode: ComposeMode, m: ExecMessage) => void;
}

const MessageBlock: React.FC<MessageBlockProps> = ({
  m, isLatest, isOutbound, downloadingId, pushingId, quickPush, onDownload, onPreview, onPush, onCompose,
}) => {
  const [showQuote, setShowQuote] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const plain = useMemo(() => trimPlainQuotes(m.bodyText || ''), [m.bodyText]);
  const htmlTrim = useMemo(() => trimHtmlQuotes(m.bodyHtml || ''), [m.bodyHtml]);
  const hasQuote = plain.hasQuote || htmlTrim.hasQuote;
  const displayText = showQuote ? (m.bodyText || m.snippet) : (plain.content || m.snippet);
  const displayHtml = useMemo(() => {
    const raw = showQuote ? m.bodyHtml : htmlTrim.content;
    return raw ? sanitize(raw) : '';
  }, [showQuote, m.bodyHtml, htmlTrim.content]);

  const attachments = m.attachments || [];

  return (
    <div className={`exec-msg${isLatest ? ' exec-msg-latest' : ''}${isOutbound ? ' exec-msg-outbound' : ''}`}>
      {isLatest && <div className="exec-msg-latest-badge">Latest</div>}
      <div className="exec-msg-head">
        <div className="exec-msg-head-left">
          <span className="exec-msg-from">{m.fromName || m.from}</span>
          {(m.to || m.cc) && (
            <div className="exec-msg-recipients">
              {m.to && (
                <span className="exec-msg-recip">
                  <span className="exec-msg-recip-label">To</span>
                  {m.to.split(',').slice(0, 4).map((addr, i) => (
                    <span key={`to-${i}`} className="exec-msg-recip-chip">{addr.trim()}</span>
                  ))}
                </span>
              )}
              {m.cc && (
                <span className="exec-msg-recip exec-msg-recip-cc">
                  <span className="exec-msg-recip-label">Cc</span>
                  {m.cc.split(',').slice(0, 3).map((addr, i) => (
                    <span key={`cc-${i}`} className="exec-msg-recip-chip">{addr.trim()}</span>
                  ))}
                </span>
              )}
            </div>
          )}
        </div>
        <span className="exec-msg-head-right">
          <span className="exec-msg-actions">
            <button type="button" className="exec-msg-action" title="Reply to this message" onClick={() => onCompose('reply', m)}>
              <Reply size={12} />
            </button>
            <button type="button" className="exec-msg-action" title="Forward this message" onClick={() => onCompose('forward', m)}>
              <Forward size={12} />
            </button>
          </span>
          <span className="exec-msg-date">{fmtMsgDate(m)}</span>
        </span>
      </div>
      <button type="button" className="exec-msg-meta" onClick={() => setShowDetails((v) => !v)}>
        to {m.to || '—'}{m.cc ? `, cc ${m.cc}` : ''}
        {showDetails ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
      </button>
      {showDetails && (
        <div className="exec-msg-detail">
          <div><span>From</span>{m.fromName ? `${m.fromName} <${m.from}>` : m.from}</div>
          <div><span>To</span>{m.to || '—'}</div>
          {m.cc && <div><span>Cc</span>{m.cc}</div>}
          <div><span>Date</span>{fmtMsgDate(m)}</div>
          {m.subject && <div><span>Subject</span>{m.subject}</div>}
        </div>
      )}
      {displayHtml
        ? <div className="exec-msg-body" dangerouslySetInnerHTML={{ __html: displayHtml }} />
        : <div className="exec-msg-body" style={{ whiteSpace: 'pre-wrap' }}>{displayText}</div>}
      {hasQuote && (
        <button type="button" className="exec-quote-toggle" onClick={() => setShowQuote((v) => !v)}>
          {showQuote ? 'Hide quoted reply' : 'Show quoted reply'}
        </button>
      )}
      {(attachments.length || m.hasAttachments) ? (
        <div className="exec-msg-attachments">
          {attachments.map((att) => (
            <div className="exec-attach-row" key={att.id}>
              <Paperclip size={12} style={{ color: 'var(--pm-text-muted)', flexShrink: 0 }} />
              <span className="exec-attach-name" title={att.filename}>{att.filename}</span>
              <span className="exec-attach-size">{fmtAttachSize(att.size)}</span>
              <div className="exec-attach-actions">
                {isPreviewable(att.mimeType) && (
                  <button
                    type="button"
                    className="exec-attach-btn"
                    title="Preview"
                    onClick={() => onPreview({ messageId: m.id, attachment: att })}
                  >
                    <Eye size={11} /> Preview
                  </button>
                )}
                <button
                  type="button"
                  className="exec-attach-btn"
                  disabled={downloadingId === att.id}
                  onClick={() => onDownload(m.id, att)}
                >
                  {downloadingId === att.id ? <Loader2 size={11} className="spin" /> : <Download size={11} />}
                  Download
                </button>
                {quickPush ? (
                  <button
                    type="button"
                    className="exec-attach-btn exec-attach-btn-primary"
                    disabled={pushingId === att.id}
                    title={`Push to ${quickPush.label}`}
                    onClick={() => quickPush.push(m.id, att)}
                  >
                    {pushingId === att.id ? <Loader2 size={11} className="spin" /> : <Upload size={11} />}
                    {quickPush.label}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="exec-attach-btn"
                  title={quickPush ? 'Choose a different destination' : 'Push to CRM'}
                  onClick={() => onPush({ messageId: m.id, messageDate: m.date, attachment: att })}
                >
                  {quickPush ? '…' : <><Upload size={11} /> Push to CRM</>}
                </button>
              </div>
            </div>
          ))}
          {attachments.length > 1 && quickPush && (
            <div className="exec-attach-row exec-attach-row-all">
              <button
                type="button"
                className="exec-attach-btn exec-attach-btn-primary"
                disabled={pushingId === '__all__'}
                onClick={async () => {
                  for (const att of attachments) {
                    // Sequential to keep server load + toasts sane.
                    // eslint-disable-next-line no-await-in-loop
                    await quickPush.push(m.id, att);
                  }
                }}
              >
                <Upload size={11} /> Push all {attachments.length} to {quickPush.label.replace(/^Push to /, '')}
              </button>
            </div>
          )}
          {!attachments.length && m.hasAttachments && (
            <div className="exec-attach-row" style={{ color: 'var(--pm-text-muted)' }}>
              Attachments present — reload thread if list is empty
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const ExecThreadPanel: React.FC<Props> = ({
  mailboxUserId, thread, stages, ownerName, ownerEmail, senderName, isOwner,
  linkCandidates, enhancedDeals, timberProjects, crmUsers, onClose, onMove, onDelete, onToast,
}) => {
  const [messages, setMessages] = useState<ExecMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEarlier, setShowEarlier] = useState(false);
  const [linkQuery, setLinkQuery] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pushingId, setPushingId] = useState<string | null>(null);
  const [pushTarget, setPushTarget] = useState<{ messageId: string; messageDate?: string; attachment: ExecAttachment } | null>(null);
  const [previewTarget, setPreviewTarget] = useState<{ messageId: string; attachment: ExecAttachment } | null>(null);
  const [composeMode, setComposeMode] = useState<ComposeMode>('reply');
  const [composeAnchorId, setComposeAnchorId] = useState<string | null>(null);
  const [composeSeq, setComposeSeq] = useState(0);
  const [injectedHtml, setInjectedHtml] = useState<{ html: string; seq: number } | null>(null);
  const [markingRead, setMarkingRead] = useState(false);
  const latestRef = useRef<HTMLDivElement>(null);
  const autoReadRef = useRef<string | null>(null);

  const ownerEmailNorm = ownerEmail.trim().toLowerCase();

  const loadThread = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getExecThread(mailboxUserId, thread.threadId);
      setMessages(sortMessagesChrono(res.messages || []));
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxUserId, thread.threadId]);

  useEffect(() => {
    setMessages([]);
    setShowEarlier(false);
    setComposeMode('reply');
    setComposeAnchorId(null);
    setInjectedHtml(null);
    loadThread();
  }, [loadThread]);

  // Auto-mark read on open (once per thread).
  useEffect(() => {
    if (thread.unread && autoReadRef.current !== thread.threadId) {
      autoReadRef.current = thread.threadId;
      markExecThreadRead(mailboxUserId, thread.threadId, false).catch(() => { /* non-blocking */ });
    }
  }, [mailboxUserId, thread.threadId, thread.unread]);

  useEffect(() => {
    if (!loading && latestRef.current) {
      latestRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [loading, messages.length]);

  const sortedMessages = useMemo(() => sortMessagesChrono(messages), [messages]);
  const earlierMessages = sortedMessages.length > 1 ? sortedMessages.slice(0, -1) : [];
  const latestMessage = sortedMessages[sortedMessages.length - 1];

  const composeAnchor = useMemo(() => {
    if (composeAnchorId) {
      const found = sortedMessages.find((m) => m.id === composeAnchorId);
      if (found) return found;
    }
    return latestMessage || null;
  }, [composeAnchorId, sortedMessages, latestMessage]);

  const fallbackReplyTo = useMemo(() => {
    for (let i = sortedMessages.length - 1; i >= 0; i--) {
      const m = sortedMessages[i];
      if (!ownerEmailNorm || m.from.toLowerCase() !== ownerEmailNorm) return m.from;
    }
    return thread.lastFrom || '';
  }, [sortedMessages, ownerEmailNorm, thread.lastFrom]);

  const stageMeta = useMemo(() => {
    const all = [{ id: EXEC_INBOX_UNSORTED_STAGE_ID, label: 'Unsorted', accent: '#94A3B8' },
      ...stages.filter((s) => !s.archived).sort((a, b) => a.order - b.order).map((s) => ({ id: s.id, label: s.label, accent: s.accent }))];
    return all;
  }, [stages]);

  const draft = thread.proposedDraft;
  const draftPending = draft?.status === 'pending';

  // One-click push destination resolved from the thread's CRM link.
  const quickPush: QuickPush | null = useMemo(() => {
    const targets = resolvePushTargets(thread.linkedTo as LinkCandidate | null, enhancedDeals, timberProjects);
    let destination: { kind: 'lead_document' | 'project_file'; leadId?: string; projectId?: string; dealId?: string } | null = null;
    let label = '';
    if (targets.defaultKind === 'project_file' && targets.canProjectFile && targets.projectId) {
      destination = { kind: 'project_file', projectId: targets.projectId };
      label = 'Push to Project';
    } else if (targets.canLeadDocument && targets.leadId) {
      destination = { kind: 'lead_document', leadId: targets.leadId, dealId: targets.dealId };
      label = 'Push to Lead';
    } else if (targets.canProjectFile && targets.projectId) {
      destination = { kind: 'project_file', projectId: targets.projectId };
      label = 'Push to Project';
    }
    if (!destination) return null;
    const dest = destination;
    return {
      label,
      push: async (messageId: string, att: ExecAttachment) => {
        setPushingId(att.id);
        try {
          await pushExecAttachment(mailboxUserId, {
            threadId: thread.threadId,
            messageId,
            attachmentId: att.id,
            filename: att.filename,
            mimeType: att.mimeType,
            destination: dest,
          });
          onToast(`${att.filename} saved to ${dest.kind === 'lead_document' ? 'Lead Documents' : 'Project Files'}`);
        } catch (e) {
          onToast((e as Error).message, true);
        } finally {
          setPushingId(null);
        }
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.linkedTo, thread.threadId, enhancedDeals, timberProjects, mailboxUserId]);

  const handleDownload = async (messageId: string, att: ExecAttachment) => {
    setDownloadingId(att.id);
    try {
      await downloadExecAttachment(mailboxUserId, messageId, att.id, att.filename);
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCompose = (mode: ComposeMode, m: ExecMessage) => {
    setComposeMode(mode);
    setComposeAnchorId(m.id);
    setComposeSeq((s) => s + 1);
  };

  const useDraft = () => {
    if (!draft) return;
    const html = draft.bodyHtml || textToHtml(draft.bodyText || '');
    setComposeMode('reply');
    setInjectedHtml((prev) => ({ html, seq: (prev?.seq || 0) + 1 }));
  };

  const discardDraft = async () => {
    try { await clearDraft(mailboxUserId, thread.threadId); onToast('Draft discarded'); }
    catch (e) { onToast((e as Error).message, true); }
  };

  const handleSent = async () => {
    if (draft) {
      try { await clearDraft(mailboxUserId, thread.threadId); } catch { /* non-blocking */ }
    }
    onToast('Sent');
    await loadThread(true);
  };

  const toggleUnread = async () => {
    setMarkingRead(true);
    try {
      await markExecThreadRead(mailboxUserId, thread.threadId, !thread.unread);
      onToast(thread.unread ? 'Marked as read' : 'Marked as unread');
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setMarkingRead(false);
    }
  };

  const gmailUrl = `https://mail.google.com/mail/?authuser=${encodeURIComponent(ownerEmail)}#all/${thread.threadId}`;

  const filteredCandidates = useMemo(() => {
    const q = linkQuery.trim().toLowerCase();
    if (!q) return linkCandidates.slice(0, 8);
    return linkCandidates.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [linkQuery, linkCandidates]);

  const setLink = async (c: LinkCandidate | null) => {
    try {
      await linkThread(mailboxUserId, thread.threadId, c);
      setLinkOpen(false);
      setLinkQuery('');
      onToast(c ? `Linked to ${c.name}` : 'Link removed');
    } catch (e) { onToast((e as Error).message, true); }
  };

  const msgProps = {
    downloadingId,
    pushingId,
    quickPush,
    onDownload: handleDownload,
    onPreview: setPreviewTarget,
    onPush: setPushTarget,
    onCompose: handleCompose,
  };

  return (
    <>
      <div className="exec-overlay" onClick={onClose} />
      <div className="exec-slideover" role="dialog" aria-label="Email thread">
        <div className="exec-so-header">
          <div className="exec-so-top">
            <div style={{ minWidth: 0 }}>
              <h2 className="exec-so-subject">{thread.subject}</h2>
              <div className="exec-so-from">
                {thread.lastFromName ? `${thread.lastFromName} · ` : ''}{thread.lastFrom}
                {thread.messageCount > 1 ? ` · ${thread.messageCount} messages` : ''}
              </div>
            </div>
            <div className="exec-so-actions">
              <button
                className="exec-so-iconbtn"
                onClick={toggleUnread}
                disabled={markingRead}
                aria-label={thread.unread ? 'Mark as read' : 'Mark as unread'}
                title={thread.unread ? 'Mark as read' : 'Mark as unread'}
              >
                {markingRead ? <Loader2 size={15} className="spin" /> : thread.unread ? <MailOpen size={15} /> : <Mail size={15} />}
              </button>
              <a
                className="exec-so-iconbtn"
                href={gmailUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in Gmail"
                title="Open in Gmail"
              >
                <ExternalLink size={15} />
              </a>
              <button
                className="exec-so-delete"
                onClick={onDelete}
                aria-label="Delete conversation"
                title="Delete conversation"
              >
                <Trash2 size={16} />
              </button>
              <button className="exec-so-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
            </div>
          </div>
          <div className="exec-so-stages">
            {stageMeta.map((s) => (
              <button
                key={s.id}
                className="exec-stage-pill"
                style={{
                  ['--pill-bg' as any]: thread.stage === s.id ? s.accent : `${s.accent}1A`,
                  ['--pill-fg' as any]: thread.stage === s.id ? '#fff' : s.accent,
                }}
                onClick={() => onMove(s.id)}
              >
                <span className="dot" style={{ background: thread.stage === s.id ? '#fff' : s.accent }} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="exec-so-linkbar">
          <Link2 size={13} />
          {thread.linkedTo ? (
            <span className="exec-link-chip">
              <span className="exec-link-type">{thread.linkedTo.type}</span>
              {thread.linkedTo.name}
              <button onClick={() => setLink(null)} aria-label="Unlink"><X size={11} /></button>
            </span>
          ) : thread.proposedLink?.status === 'pending' ? (
            <span
              className="exec-row-link-suggest"
              title={thread.proposedLink.rationale || `Agent suggests linking to ${thread.proposedLink.target.name}`}
            >
              <Sparkles size={11} />
              Agent suggests: <strong>{thread.proposedLink.target.name}</strong>
              {typeof thread.proposedLink.confidence === 'number' && (
                <em>{Math.round(thread.proposedLink.confidence * 100)}%</em>
              )}
              <button
                type="button"
                className="accept"
                title="Accept link"
                onClick={async () => {
                  try {
                    await acceptProposedLink(mailboxUserId, thread.threadId, thread.proposedLink!.target);
                    onToast(`Linked to ${thread.proposedLink!.target.name}`);
                  } catch (e) { onToast((e as Error).message, true); }
                }}
              >
                ✓
              </button>
              <button
                type="button"
                className="dismiss"
                title="Dismiss suggestion"
                onClick={async () => {
                  try {
                    await dismissProposedLink(mailboxUserId, thread.threadId);
                    onToast('Suggestion dismissed');
                  } catch (e) { onToast((e as Error).message, true); }
                }}
              >
                <X size={11} />
              </button>
            </span>
          ) : (
            <span className="exec-link-search">
              <input
                className="exec-link-input"
                placeholder="Link to a lead, deal or project…"
                value={linkQuery}
                onFocus={() => setLinkOpen(true)}
                onChange={(e) => { setLinkQuery(e.target.value); setLinkOpen(true); }}
              />
              {linkOpen && filteredCandidates.length > 0 && (
                <div className="exec-link-results">
                  {filteredCandidates.map((c) => (
                    <button key={`${c.type}-${c.id}`} className="exec-link-result" onClick={() => setLink(c)}>
                      <span className="exec-link-type">{c.type}</span>
                      <span>{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          )}
        </div>

        <div className="exec-so-body">
          {loading && (
            <div className="exec-empty"><Loader2 size={20} className="spin" /><p>Loading conversation…</p></div>
          )}

          {!loading && earlierMessages.length > 0 && (
            <div className="exec-msg-older-wrap">
              <button
                type="button"
                className="exec-msg-older-toggle"
                onClick={() => setShowEarlier((v) => !v)}
              >
                {showEarlier ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {showEarlier
                  ? 'Hide earlier messages'
                  : `${earlierMessages.length} earlier message${earlierMessages.length === 1 ? '' : 's'}`}
              </button>
              {showEarlier && (
                <div className="exec-thread-messages">
                  {earlierMessages.map((m) => (
                    <MessageBlock
                      key={m.id}
                      m={m}
                      isOutbound={!!ownerEmailNorm && m.from.toLowerCase() === ownerEmailNorm}
                      {...msgProps}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!loading && latestMessage && (
            <div className="exec-thread-messages" ref={latestRef}>
              <MessageBlock
                m={latestMessage}
                isLatest
                isOutbound={!!ownerEmailNorm && latestMessage.from.toLowerCase() === ownerEmailNorm}
                {...msgProps}
              />
            </div>
          )}

          {draftPending && (
            <div className="exec-draft-card">
              <div className="exec-draft-head">
                <Sparkles size={14} style={{ color: '#7C3AED' }} />
                <span className="exec-draft-title">Agent suggested a reply</span>
                {typeof draft?.confidence === 'number' && (
                  <span className="exec-draft-conf">{Math.round((draft.confidence || 0) * 100)}% confidence</span>
                )}
              </div>
              {draft?.note && <div className="exec-draft-note">“{draft.note}”</div>}
              <div className="exec-draft-preview">
                {draft?.bodyText || (draft?.bodyHtml ? draft.bodyHtml.replace(/<[^>]+>/g, '') : '')}
              </div>
              <div className="exec-draft-actions">
                <button className="exec-ghost-btn" onClick={useDraft}><FileText size={13} /> Use draft</button>
                <button className="exec-ghost-btn danger" onClick={discardDraft}><Trash2 size={13} /> Discard</button>
              </div>
            </div>
          )}
        </div>

        <ExecComposer
          mailboxUserId={mailboxUserId}
          threadId={thread.threadId}
          threadSubject={thread.subject}
          anchor={composeAnchor}
          fallbackReplyTo={fallbackReplyTo}
          mode={composeMode}
          onModeChange={(m) => { setComposeMode(m); setComposeSeq((s) => s + 1); }}
          composeSeq={composeSeq}
          ownerName={ownerName}
          ownerEmail={ownerEmail}
          senderName={senderName}
          isOwner={isOwner}
          stages={stages}
          injectedHtml={injectedHtml}
          onSent={handleSent}
          onToast={onToast}
        />
      </div>

      {pushTarget && (
        <ExecAttachmentPushModal
          open
          mailboxUserId={mailboxUserId}
          threadId={thread.threadId}
          threadSubject={thread.subject}
          emailThreadId={thread.threadId}
          messageId={pushTarget.messageId}
          messageDate={pushTarget.messageDate}
          attachment={pushTarget.attachment}
          linkedTo={thread.linkedTo}
          linkCandidates={linkCandidates}
          deals={enhancedDeals}
          timberProjects={timberProjects}
          crmUsers={crmUsers}
          onClose={() => setPushTarget(null)}
          onToast={onToast}
        />
      )}

      {previewTarget && (
        <ExecAttachmentPreviewModal
          mailboxUserId={mailboxUserId}
          messageId={previewTarget.messageId}
          attachment={previewTarget.attachment}
          onClose={() => setPreviewTarget(null)}
          onToast={onToast}
        />
      )}
    </>
  );
};

export default ExecThreadPanel;
